import { Select, Form, Button } from 'antd';
import { useCallback, useState } from 'react';

import { type ModelTag, useUpdateTagMutation } from '@app/api/Tags';
import type { TagSelectType } from '@app/components/TagsSelect/types/';
import { Tag } from '@app/components/TagsSelect/ui/Tag';
import { useUserPrint } from '@app/hooks';
import { AddTagsModal } from '@app/pages/Tags/components';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { withTestId } from '@app/utils';

import '@app/components/TagsSelect/styles/TagSelect.desktop.scss';
const { useForm } = Form;
const DEFAULT_ICON = 'fa-solid fa-tag';

export const TagSelect: TagSelectType = ({
  options,
  tagTestId,
  dataTestId,
  dropdownDataTestId,
  addable,
  afterAddTag,
  ...props
}) => {
  const [addTagForm] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTagLoading, setIsAddTagLoading] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(DEFAULT_ICON);
  const { activeBranchId, userId, database } = useUserPrint();

  const { showNotification } = useNotification();
  const [updateTag] = useUpdateTagMutation();

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    addTagForm.resetFields();
    setSelectedIcon(DEFAULT_ICON);
  }, [addTagForm]);

  const openCreateTagModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const submitMainForm = useCallback(async () => {
    const values = addTagForm.getFieldsValue();
    const tagModel: ModelTag = {
      Id: 0,
      Name: values.Name,
      Color:
        typeof values.Color === 'string'
          ? values.Color
          : values.Color?.metaColor
          ? values.Color.metaColor.toHexString()
          : '#1677FF',
      IconId: selectedIcon,
      Description: values.Description,
      IsDeleted: false,
      IsSelected: false,
      IsSystem: false,
      SystemId: null,
    };
    setIsAddTagLoading(true);

    try {
      const response = await updateTag({
        ActiveBranchId: activeBranchId,
        data: tagModel,
        logs: { isLogged: true, userId, database },
      }).unwrap();

      setIsModalOpen(false);
      showNotification('Тег обновлен успешно', 'success');
      setSelectedIcon('fa-solid fa-tag');
      addTagForm.resetFields();

      afterAddTag?.(response);
    } catch (error) {
      showNotification(error);
    } finally {
      setIsAddTagLoading(false);
    }
  }, [
    addTagForm,
    selectedIcon,
    updateTag,
    activeBranchId,
    userId,
    database,
    showNotification,
    afterAddTag,
  ]);

  return (
    <>
      <Select
        mode="multiple"
        rootClassName="tag-select-root-class"
        options={options}
        allowClear
        {...(dataTestId && { 'data-testid': dataTestId })}
        filterOption={(input, option) => {
          const label = option?.label as string;
          return label?.toLowerCase().includes(input.toLowerCase());
        }}
        optionRender={(option) => {
          const currentOption = (options || []).find(
            (opt) => opt.value === option.value
          );
          return (
            <Tag
              testId={tagTestId}
              label={option.label}
              value={option.value as number}
              color={currentOption?.color}
              backgroundColor={currentOption?.backgroundColor}
              icon={currentOption?.icon}
              closable={false}
            />
          );
        }}
        tagRender={({ value, ...props }) => {
          const currentOption = (options || []).find(
            (option) => option.value === value
          );
          return (
            <Tag
              {...props}
              value={value as number}
              color={currentOption?.color}
              backgroundColor={currentOption?.backgroundColor}
              icon={currentOption?.icon}
              testId={'name-selected-tag'}
            />
          );
        }}
        popupRender={(menu) => (
          <>
            {addable && (
              <Button
                className="add-tag-button"
                type="link"
                size="small"
                onClick={openCreateTagModal}
              >
                Добавить тег
              </Button>
            )}
            {dropdownDataTestId ? withTestId(dropdownDataTestId)(menu) : menu}
          </>
        )}
        {...(dropdownDataTestId && {
          popupRender: withTestId(dropdownDataTestId),
        })}
        {...props}
      />
      <AddTagsModal
        currentTagId={0}
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        form={addTagForm}
        submitMainForm={submitMainForm}
        setSelectedIcon={setSelectedIcon}
        selectedIcon={selectedIcon}
        disabled={isAddTagLoading}
      />
    </>
  );
};
